import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux'
import { RootState } from '../../root.reducer'
import { useLocation } from 'react-router-dom'

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

export const pushToGTM = (data: Record<string, any>) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
};

export const useGoogleTagManager = () => {
  const location = useLocation();
  const prevPath = useRef<string>('');
  const userId = useSelector((s: RootState) => s?.user?.userId);
  const activeRobot = useSelector((s: RootState) => s?.robots[0]);
  const env = process.env.REACT_APP_ENV || process.env.NODE_ENV;
  
  useEffect(() => {
    const gtmId = process.env.REACT_APP_GTM_ID;
    const auth = process.env.REACT_APP_GTM_AUTH;
    const preview = process.env.REACT_APP_GTM_PREVIEW;
    const isProd = env === 'production';
    const dataLayerName = 'dataLayer';

    if (!gtmId) {
      console.warn('GTM: Missing GTM ID.');
      return;
    }

    window[dataLayerName] = window[dataLayerName] || [];
    window[dataLayerName].push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    const dlParam = dataLayerName !== 'dataLayer' ? `&l=${dataLayerName}` : '';
    const script = document.createElement('script');
    script.async = true;

    if (!isProd && auth && preview) {
      script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}&gtm_auth=${auth}&gtm_preview=${preview}${dlParam}&gtm_cookies_win=x`;
    } else if (isProd) {
      script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}${dlParam}`;
    }

    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [env]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
    const target = (event.target as HTMLElement)?.closest('[data-gtm-id]');
    if (!target) return;

    const gtmId = target.getAttribute('data-gtm-id');
    if (!gtmId) return;

      pushToGTM({
        event: 'button_click',
        elementId: gtmId || '',
      });
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  useEffect(() => {
    pushToGTM({
      appUserId: userId,
      robotId: activeRobot?.id,
      environment: env,
    });
  }, [userId, activeRobot?.id, env]);

useEffect(() => {
  const searchParams = new URLSearchParams(location.search);

  const utm_campaign = searchParams.get('utm_campaign') || undefined;
  const utm_medium = searchParams.get('utm_medium') || undefined;
  const utm_source = searchParams.get('utm_source') || undefined;

  if (utm_source || utm_medium || utm_campaign) {
    pushToGTM({
      utm_source,
      utm_medium,
      utm_campaign,
    });
  }

  pushToGTM({
    event: 'page_view',
    page_path: location.pathname + location.search,
    prev_page_path: prevPath.current || null,
  });

  prevPath.current = location.pathname + location.search;
}, [location.pathname, location.search]);

};
